import { ApiError } from '../api.js';
import { attachPhoto, pushSyncBatch, uploadFile, type SyncOutcome } from '../lesson-api.js';
import {
  clearOutboxEntry,
  clearQueuedPhoto,
  isOutboxAvailable,
  listOutbox,
  listQueuedPhotos,
  markAttemptFailed,
  markPhotoFailed,
} from './outbox.js';

/**
 * Draining the outbox.
 *
 * Runs when the browser regains connectivity, when the app starts, and when the
 * user asks. The server does the merging; this side's only jobs are to send what
 * is queued, to interpret the per-record outcome, and — the part that matters —
 * to be careful about what it throws away.
 *
 * **An entry is removed only when the server has accounted for it.** A merge and
 * a conflict both count: a conflict means the server has the device's value
 * safely in a conflict row awaiting a human, so keeping it queued would re-send
 * work that already arrived. A network failure counts for nothing and the queue
 * is left untouched.
 *
 * **Order matters.** Lessons go first, photos second, because a photo names the
 * form it belongs to and the server will not attach one to a record it has never
 * seen.
 */

/** Rule 1: the cap is a setting. The server independently enforces its own. */
const BATCH_SIZE = Number(import.meta.env.VITE_SYNC_BATCH_SIZE ?? 50);

/**
 * How many times a rejected entry is retried before it is left alone.
 *
 * A 4xx will not pass on the tenth attempt any more than the first, and retrying
 * it every time the phone finds a signal buries the entries that WOULD succeed.
 * The entry stays in the outbox and is surfaced to the user instead of discarded.
 */
const MAX_ATTEMPTS = Number(import.meta.env.VITE_SYNC_MAX_ATTEMPTS ?? 5);

export interface FlushReport {
  attempted: number;
  created: number;
  merged: number;
  conflicted: number;
  photosUploaded: number;
  failed: number;
  /** Entries that have failed often enough to need a human. */
  stuck: { id: string; error: string }[];
  offline: boolean;
}

const EMPTY: FlushReport = {
  attempted: 0,
  created: 0,
  merged: 0,
  conflicted: 0,
  photosUploaded: 0,
  failed: 0,
  stuck: [],
  offline: false,
};

let inFlight: Promise<FlushReport> | null = null;

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/**
 * Flush the queue.
 *
 * Concurrent calls share one run. Both the `online` event and the app's own
 * startup can fire within a few milliseconds of each other, and two flushes
 * racing would send the same batch twice.
 */
export function flushOutbox(): Promise<FlushReport> {
  if (inFlight) return inFlight;
  inFlight = runFlush().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runFlush(): Promise<FlushReport> {
  if (!isOutboxAvailable()) return EMPTY;
  if (!isOnline()) return { ...EMPTY, offline: true };

  const report: FlushReport = { ...EMPTY, stuck: [] };

  const queued = (await listOutbox()).filter((entry) => {
    if (entry.attempts < MAX_ATTEMPTS) return true;
    report.stuck.push({ id: entry.id, error: entry.lastError ?? 'Repeatedly rejected.' });
    return false;
  });

  for (let index = 0; index < queued.length; index += BATCH_SIZE) {
    const slice = queued.slice(index, index + BATCH_SIZE);
    report.attempted += slice.length;

    const records = slice.map((entry) => ({
      id: entry.id,
      ...(entry.create ? { create: entry.create } : {}),
      ...(entry.changes && Object.keys(entry.changes).length > 0
        ? { changes: entry.changes }
        : {}),
      clientUpdatedAt: entry.clientUpdatedAt,
    }));

    let result;
    try {
      result = await pushSyncBatch(records);
    } catch (error) {
      // The whole batch failed — a network drop mid-sync, or the server refusing
      // the request shape. Nothing is discarded.
      const message = error instanceof Error ? error.message : 'Sync failed.';
      report.failed += slice.length;
      for (const entry of slice) await markAttemptFailed(entry.id, message);
      if (error instanceof ApiError && error.status >= 500) continue;
      if (!isOnline()) return { ...report, offline: true };
      continue;
    }

    for (const outcome of result.outcomes) {
      await applyOutcome(outcome, report);
    }
  }

  report.photosUploaded = await flushPhotos(report);
  return report;
}

async function applyOutcome(outcome: SyncOutcome, report: FlushReport): Promise<void> {
  switch (outcome.status) {
    case 'created':
      report.created += 1;
      await clearOutboxEntry(outcome.id);
      return;
    case 'merged':
      report.merged += 1;
      await clearOutboxEntry(outcome.id);
      return;
    case 'conflict':
      // The server holds both values in a conflict row. The device's copy is not
      // lost, so re-sending it would only create noise.
      report.conflicted += 1;
      await clearOutboxEntry(outcome.id);
      return;
    case 'not_found':
      // Archived on the server while this device was offline. Retrying will never
      // succeed, so the entry is marked rather than looped.
      report.failed += 1;
      await markAttemptFailed(outcome.id, 'This lesson no longer exists on the server.');
      report.stuck.push({
        id: outcome.id,
        error: 'This lesson no longer exists on the server.',
      });
      return;
    case 'rejected': {
      // Outside this user's scope, or no longer editable (submitted or
      // approved). Sending it again cannot change the answer, so it is kept on
      // the device — nothing is lost — and marked for a person to look at.
      const reason = outcome.reason ?? 'The server would not accept this change.';
      report.failed += 1;
      await markAttemptFailed(outcome.id, reason);
      report.stuck.push({ id: outcome.id, error: reason });
      return;
    }
    default:
      // A status this build does not know. Leaving the entry untouched would
      // resend it on every flush for ever, so it is counted as a failed attempt.
      report.failed += 1;
      await markAttemptFailed(outcome.id, `Unrecognised sync outcome: ${String((outcome as { status: unknown }).status)}`);
  }
}

/**
 * Upload queued photos.
 *
 * Two steps per photo — the bytes, then the attachment — because the storage
 * layer and the lesson record are separate concerns (ADR-0013). If the upload
 * lands and the attach fails, the photo is retried whole: a duplicate blob is
 * cheap and content-hash keys mean the second upload of identical bytes is the
 * same object anyway.
 */
async function flushPhotos(report: FlushReport): Promise<number> {
  let uploaded = 0;

  for (const photo of await listQueuedPhotos()) {
    if (photo.attempts >= MAX_ATTEMPTS) {
      report.stuck.push({
        id: photo.localId,
        error: photo.lastError ?? 'This photo was repeatedly rejected.',
      });
      continue;
    }

    try {
      const file = await uploadFile({
        filename: photo.filename,
        contentType: photo.contentType,
        data: photo.data,
        purpose: 'lesson_photo',
      });

      await attachPhoto(photo.formId, {
        category: photo.category,
        fileId: file.id,
        caption: photo.caption,
        capturedAt: photo.capturedAt,
      });

      await clearQueuedPhoto(photo.localId);
      uploaded += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The photo could not be uploaded.';
      await markPhotoFailed(photo.localId, message);
      report.failed += 1;
      if (!isOnline()) break;
    }
  }

  return uploaded;
}

/**
 * Flush whenever the browser says it is back online.
 *
 * `navigator.onLine` is optimistic — it reports the network interface, not
 * whether anything is reachable — so a flush that fails leaves the queue intact
 * and the next event tries again. Returns an unsubscribe for React's cleanup.
 */
export function watchConnectivity(onFlushed: (report: FlushReport) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = () => {
    void flushOutbox().then(onFlushed);
  };

  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}
